/**
 * LessonBuilderService — bottone "+" sul tool per il lesson-builder.
 *
 * Attivazione: serve la combo
 *   - URL `role=docente` (default in standalone, esplicito dall'host nel bridge)
 *   - URL contains `userId=<uuid>` (lessonId opzionale → lazy create)
 *   - URL optionally `lessonBuilderApi=<http://...>` (default http://localhost:8100)
 *
 * Esempio per testing in standalone:
 *   http://localhost:5180/?role=docente
 *     &userId=33333333-3333-3333-3333-333333333301
 *     &lessonId=f346fe7a-2ec0-4ad6-8f91-799fa66bb7e8
 *
 * Quando attivo:
 *   - aggiunge `data-lesson-builder-active` su <body>
 *   - inietta veri <button data-lb-add> dentro ogni [data-fragment-id]
 *   - MutationObserver: i bottoni si attaccano anche su contenuti renderizzati dopo
 *   - Click bottone → POST /v2/contents/{lessonId}/blocks + toast + flash
 *
 * NB (filosofia): la cattura MAPPE è GRACEFULLY OPTIONAL. Le viste mappa di
 * filosofia (AuthorMapView/ArgumentMapView) non espongono ancora l'API di
 * selezione `__lbMapSelection`; finché non lo fanno, i bottoni mappa si
 * limitano alla cattura dell'SVG intero. La cattura TESTO funziona comunque.
 */

import { getI18n } from '@/i18n/i18n-registry';
type BlockType = 'paragraph' | 'heading' | 'image' | 'link' | 'quote' | 'list' | 'map';

/** Snapshot minimale del grafo mappa (compatibile con l'API che le viste
 *  esporranno in una fase successiva). */
interface MapGraphSnapshot {
  kind: string;
  authorId: string;
  locale: string;
  viewBox: [number, number, number, number];
  nodes: Array<{ id: string; label: string; type: string; x: number; y: number; w: number; h: number; note?: string }>;
  edges: Array<{ from: string; to: string }>;
}

/** Cattura pronta per il blocco 'map': SVG ritagliato + payload strutturato. */
interface MapCapture {
  content_html: string;
  data: Record<string, unknown>;
}

/** API di selezione "a pezzi" opzionalmente esposta da una vista mappa.
 *  Definita LOCALMENTE: in filosofia nessuna vista la esporta ancora. */
interface MapSelectionApi {
  enterSelectMode: () => void;
  exitSelectMode: () => void;
  isSelectMode: () => boolean;
  getSelectedIds: () => string[];
  clearSelection: () => void;
  onSelectionChange: (cb: (ids: string[]) => void) => void;
  getGraph: () => MapGraphSnapshot;
  buildCapture: () => MapCapture | null;
}

interface LessonBuilderConfig {
  apiUrl: string;
  userId: string;
  /** null = modalità LAZY: il docente è entrato nel tool SENZA una lezione
   *  (link diretto da PC/telefono). La lezione viene CREATA al primo
   *  "Componi lezione" e ricordata in sessionStorage. */
  lessonId: string | null;
}

export class LessonBuilderService {
  /** Identità del tool, presa dal manifest: finisce in `source.tool` di ogni
   *  blocco catturato ed è la chiave con cui l'hub sa da dove riaprirlo.
   *  NON va scritta a mano qui: un tool clonato erediterebbe l'id dell'originale
   *  e dichiarerebbe di essere un altro (successo davvero, vedi la checklist). */
  private toolId = 'sconosciuto';
  private config: LessonBuilderConfig | null = null;
  /** Materia, da `manifest.subject`: intitola le lezioni create al volo. */
  private subject = '';
  private observer: MutationObserver | null = null;
  // Mappa fragmentId(URI) → block_id già presente nella lezione, per il toggle add/remove.
  private blocksByUri = new Map<string, string>();

  init(toolId: string, subject: string): void {
    this.subject = subject;
    if (!toolId) {
      console.error('[lesson-builder] manca l\'id del tool (manifest.id): cattura disattivata');
      return;
    }
    this.toolId = toolId;
    const params = new URLSearchParams(window.location.search);
    const role = params.get('role') ?? 'docente';
    if (role !== 'docente') return;

    const userId = params.get('userId');
    if (!userId) {
      console.info('[lesson-builder] inactive — userId not in URL');
      return;
    }

    const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    if (!UUID_RE.test(userId)) {
      console.error('[lesson-builder] URL non valido — userId non è un UUID completo', { userId });
      return;
    }

    // lessonId OPZIONALE: se manca, il docente è entrato nel tool "diretto"
    // (da PC/telefono): la lezione si crea al primo "Componi lezione" —
    // oppure si riusa quella già creata in questa sessione (sessionStorage).
    let lessonId = params.get('lessonId');
    if (lessonId && !UUID_RE.test(lessonId)) {
      console.error('[lesson-builder] URL non valido — lessonId non è un UUID completo', { lessonId });
      document.body.dataset.lessonBuilderActive = 'true';
      setTimeout(() => this.toast('URL incompleto: lessonId troncato. Riapri dal pulsante "Cattura dal tool".', 'error'), 600);
      return;
    }
    if (!lessonId) {
      const saved = sessionStorage.getItem(`lb_lesson_${userId}`);
      if (saved && UUID_RE.test(saved)) lessonId = saved;
    }

    this.config = {
      apiUrl: params.get('lessonBuilderApi') ?? 'http://localhost:8100',
      userId,
      lessonId,
    };

    document.body.dataset.lessonBuilderActive = 'true';
    // Modalità OFF di default: docente la attiva esplicitamente via toggle.
    // In classe il tool resta pulito (no tastini visibili).
    const savedMode = sessionStorage.getItem('lb_mode') === 'on';
    if (savedMode && this.config.lessonId) document.body.dataset.lbMode = 'on';
    this.mountToggle();
    this.attachAllButtons(document.body);
    this.attachMapCaptureButtons(document.body);
    this.startObserver();
    if (this.config.lessonId) {
      this.mountStatusBadge();
      this.syncFromServer();
    }
    console.info('[lesson-builder] activated — lessonId:', this.config.lessonId ?? '(lazy)', 'mode:', savedMode ? 'on' : 'off');
  }

  /** Garantisce una lezione su cui catturare: se manca (ingresso diretto nel
   *  tool) la CREA sul backend e la ricorda per la sessione. */
  private async ensureLesson(): Promise<boolean> {
    if (!this.config) return false;
    if (this.config.lessonId) return true;
    try {
      const today = new Date().toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' });
      const r = await fetch(`${this.config.apiUrl}/v2/contents`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-User-Id': this.config.userId },
        body: JSON.stringify({ title: `${this.subject} · ${today}` }),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const data = (await r.json()) as { id: string };
      this.config.lessonId = data.id;
      sessionStorage.setItem(`lb_lesson_${this.config.userId}`, data.id);
      if (!this.statusBadge) this.mountStatusBadge();
      this.syncFromServer();
      this.toast(this.t('lessonBuilder.toast.lessonCreated'), 'success');
      return true;
    } catch (err) {
      console.error('[lesson-builder] creazione lezione fallita:', err);
      this.toast(this.t('lessonBuilder.toast.lessonFailed'), 'error');
      return false;
    }
  }

  private mountToggle(): void {
    const toggle = document.createElement('button');
    toggle.type = 'button';
    toggle.className = 'lb-toggle';
    toggle.innerHTML = `
      <span class="lb-toggle__dot"></span>
      <span class="lb-toggle__label">${this.t('lessonBuilder.toggle')}</span>
    `;
    toggle.addEventListener('click', async () => {
      const isOn = document.body.dataset.lbMode === 'on';
      if (isOn) {
        delete document.body.dataset.lbMode;
        sessionStorage.removeItem('lb_mode');
        return;
      }
      // Ingresso diretto senza lezione: la si crea ORA (primo attivamento).
      const ok = await this.ensureLesson();
      if (!ok) return;
      document.body.dataset.lbMode = 'on';
      sessionStorage.setItem('lb_mode', 'on');
    });
    document.body.appendChild(toggle);
  }

  private attachMapCaptureButtons(root: ParentNode): void {
    // Cerca container delle viste mappa note.
    // Ogni view che contiene un <svg> grande è candidata.
    const views = root.querySelectorAll<HTMLElement>(
      '.influence-graph-view, .theme-map-view, .author-map-view, .argument-map-view, [data-map-view]',
    );
    views.forEach((v) => this.attachMapButtonTo(v));
  }

  /** Recupera l'API di selezione agganciata dalla view mappa (se presente).
   *  In filosofia nessuna vista la espone ancora → di norma ritorna null. */
  private getMapSelection(view: HTMLElement): MapSelectionApi | null {
    return (view as unknown as { __lbMapSelection?: MapSelectionApi }).__lbMapSelection ?? null;
  }

  private attachMapButtonTo(view: HTMLElement): void {
    // NON fidarsi del flag dataset: le viste si ridisegnano con innerHTML=...
    // che SPAZZA i bottoni ma il flag resta sull'elemento radice → i tool
    // sparivano per sempre dopo un re-render (es. cambio lente, navigazione).
    // La verità è il DOM: se i tools non ci sono più, si riattaccano.
    if (view.querySelector(':scope > .lb-map-tools')) return;
    // Nessun <svg> = niente da catturare (view mappa non ancora renderizzata).
    if (!view.querySelector('svg')) return;
    view.dataset.lbMapAttached = 'true';
    if (getComputedStyle(view).position === 'static') view.style.position = 'relative';

    const tools = document.createElement('div');
    tools.className = 'lb-map-tools';

    // 1) Cattura mappa INTERA (sempre disponibile)
    const btnWhole = document.createElement('button');
    btnWhole.type = 'button';
    btnWhole.className = 'lb-map-capture-btn';
    btnWhole.innerHTML = `<span>+</span><span>${this.t('lessonBuilder.addMap')}</span>`;
    btnWhole.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.captureMap(view).catch((err) => {
        console.error('[lesson-builder] map capture failed:', err);
        this.toast(this.t('lessonBuilder.toast.mapFailed'), 'error');
      });
    });
    tools.appendChild(btnWhole);

    // 2) Selezione "a pezzi" — solo se la view espone l'API di selezione.
    //    (Fase successiva in filosofia: oggi `sel` è sempre null.)
    const sel = this.getMapSelection(view);
    if (sel) {
      const labelOf = (b: HTMLButtonElement) => b.querySelector('span:last-child')!;

      const btnAddSel = document.createElement('button');
      btnAddSel.type = 'button';
      btnAddSel.className = 'lb-map-capture-btn lb-map-sel-add lb-is-hidden';
      btnAddSel.disabled = true;
      btnAddSel.innerHTML = `<span>+</span><span>${this.t('lessonBuilder.addSelection')}</span>`;

      const btnToggle = document.createElement('button');
      btnToggle.type = 'button';
      btnToggle.className = 'lb-map-capture-btn lb-map-sel-toggle';
      btnToggle.innerHTML = `<span>⛶</span><span>${this.t('lessonBuilder.selectPieces')}</span>`;

      const exitSelectUi = () => {
        btnToggle.classList.remove('is-active');
        labelOf(btnToggle).textContent = this.t('lessonBuilder.selectPieces');
        btnAddSel.classList.add('lb-is-hidden');
      };

      btnToggle.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (sel.isSelectMode()) {
          sel.exitSelectMode();
          exitSelectUi();
        } else {
          sel.enterSelectMode();
          btnToggle.classList.add('is-active');
          labelOf(btnToggle).textContent = this.t('lessonBuilder.cancelSelection');
          btnAddSel.classList.remove('lb-is-hidden');
        }
      });

      sel.onSelectionChange((ids) => {
        const n = ids.length;
        btnAddSel.disabled = n === 0;
        labelOf(btnAddSel).textContent = n === 0
          ? this.t('lessonBuilder.addSelection')
          : this.t('lessonBuilder.addSelectionCount', { count: n });
      });

      btnAddSel.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.captureMapSelection(view, sel)
          .then(() => { sel.exitSelectMode(); exitSelectUi(); })
          .catch((err) => {
            console.error('[lesson-builder] map selection capture failed:', err);
            this.toast(this.t('lessonBuilder.toast.selectionFailed'), 'error');
          });
      });

      tools.appendChild(btnToggle);
      tools.appendChild(btnAddSel);
    }

    view.appendChild(tools);
  }

  /** Nome della view per l'URI di provenienza. */
  private viewKindOf(view: HTMLElement): string {
    return view.classList.contains('influence-graph-view')
      ? 'influence-graph'
      : view.classList.contains('theme-map-view')
        ? 'theme-map'
        : view.classList.contains('author-map-view')
          ? 'author-map'
          : view.classList.contains('argument-map-view')
            ? 'argument-map'
            : 'map';
  }

  /** Clona l'SVG per la cattura: ripulisce stili inline di selezione + xmlns. */
  private cleanCloneSvg(svg: SVGSVGElement): SVGSVGElement {
    const clone = svg.cloneNode(true) as SVGSVGElement;
    clone.querySelectorAll<SVGGElement>('.graph-node').forEach((g) => {
      g.style.filter = '';
      g.style.opacity = '';
    });
    if (!clone.getAttribute('xmlns')) clone.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
    return clone;
  }

  /** POST di un blocco; aggiorna il contatore del badge. */
  private async postBlock(payload: unknown): Promise<void> {
    if (!this.config?.lessonId) return;
    const url = `${this.config.apiUrl}/v2/contents/${this.config.lessonId}/blocks`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-User-Id': this.config.userId },
      body: JSON.stringify(payload),
    });
    if (!response.ok) {
      const text = await response.text().catch(() => '');
      throw new Error(`HTTP ${response.status}: ${text}`);
    }
    const data = (await response.json()) as { body_blocks?: { blocks?: unknown[] } };
    const count = data.body_blocks?.blocks?.length ?? 0;
    if (this.countEl) {
      this.countEl.textContent = String(count);
      this.statusBadge?.classList.add('lb-pulse');
      setTimeout(() => this.statusBadge?.classList.remove('lb-pulse'), 400);
    }
  }

  /** Cattura la mappa INTERA come blocco 'map' (selection 'full'). */
  private async captureMap(view: HTMLElement): Promise<void> {
    if (!this.config?.lessonId) return;
    const svg = view.querySelector('svg');
    if (!svg) {
      this.toast(this.t('lessonBuilder.toast.noMap'), 'error');
      return;
    }
    const viewKind = this.viewKindOf(view);
    const clone = this.cleanCloneSvg(svg as SVGSVGElement);
    const html = `<figure class="lb-map">${clone.outerHTML}</figure>`;

    // Payload strutturato (L3-ready): se la view espone il grafo, lo includiamo.
    const sel = this.getMapSelection(view);
    let data: Record<string, unknown>;
    if (sel) {
      const g = sel.getGraph();
      data = {
        kind: g.kind, author_id: g.authorId, locale: g.locale, selection: 'full',
        node_ids: g.nodes.map((n) => n.id), nodes: g.nodes, edges: g.edges, viewBox: g.viewBox,
      };
    } else {
      data = { kind: viewKind, selection: 'full' };
    }

    await this.postBlock({
      type: 'map' as const,
      content_html: html,
      source: { tool: this.toolId, uri: `map:${viewKind}`, captured_at: new Date().toISOString() },
      data,
    });
    this.toast(`✓ Mappa "${viewKind}" aggiunta`, 'success');
  }

  /** Cattura il livello corrente (nodi mappa o step del drill-in) come blocco 'map' ritagliato. */
  private async captureMapSelection(_view: HTMLElement, sel: MapSelectionApi): Promise<void> {
    if (!this.config?.lessonId) return;
    const cap = sel.buildCapture();
    if (!cap) {
      this.toast(this.t('lessonBuilder.toast.selectOne'), 'error');
      return;
    }
    const n = sel.getSelectedIds().length;
    await this.postBlock({
      type: 'map' as const,
      content_html: cap.content_html,
      source: { tool: this.toolId, uri: 'map:author-map#sel', captured_at: new Date().toISOString() },
      data: cap.data,
    });
    this.toast(this.t('lessonBuilder.toast.selectionAdded', { count: n }), 'success');
  }

  private statusBadge: HTMLElement | null = null;
  private countEl: HTMLElement | null = null;

  private mountStatusBadge(): void {
    const badge = document.createElement('div');
    badge.className = 'lb-status-badge';
    badge.innerHTML = `
      <span>Lezione attiva</span>
      <span class="lb-status-badge__count">0</span>
      <button type="button" class="lb-status-badge__link" data-lb-open-editor>Apri editor</button>
    `;
    document.body.appendChild(badge);
    this.statusBadge = badge;
    this.countEl = badge.querySelector('.lb-status-badge__count');
    badge.querySelector('[data-lb-open-editor]')?.addEventListener('click', () => {
      if (!this.config?.lessonId) return;
      const hubUrl = new URLSearchParams(window.location.search).get('hubUrl') ?? 'http://localhost:3000';
      // Rotta editor reale del hub (Next.js): /my-content/edit/<lessonId>.
      // Navigazione nella STESSA scheda: niente nuova tab per ogni passaggio,
      // il tasto Indietro del browser riporta al tool.
      window.location.assign(`${hubUrl}/my-content/edit/${this.config.lessonId}`);
    });
  }

  /** Legge la lezione dal server, costruisce la mappa URI→block_id, conta e ri-marca i "+". */
  private async syncFromServer(): Promise<void> {
    if (!this.config?.lessonId) return;
    try {
      const r = await fetch(`${this.config.apiUrl}/v2/contents/${this.config.lessonId}`, {
        headers: { 'X-User-Id': this.config.userId },
      });
      if (!r.ok) return;
      const data = (await r.json()) as {
        body_blocks?: { blocks?: { block_id: string; source?: { uri?: string } }[] };
      };
      const blocks = data.body_blocks?.blocks ?? [];
      this.blocksByUri.clear();
      for (const b of blocks) {
        if (b.source?.uri) this.blocksByUri.set(b.source.uri, b.block_id);
      }
      this.setCount(blocks.length);
      this.remarkAllButtons();
    } catch {
      // silent — non blocchiamo il tool se il backend è giù
    }
  }

  private setCount(n: number): void {
    if (this.countEl) this.countEl.textContent = String(n);
  }

  /** Aggiorna lo stato visivo "aggiunto" su tutti i "+" in base alla mappa corrente. */
  private remarkAllButtons(): void {
    document.querySelectorAll<HTMLElement>('[data-fragment-id]').forEach((el) => {
      const uri = el.dataset.fragmentId;
      const btn = el.querySelector<HTMLElement>(':scope > .lb-add-btn');
      if (!uri || !btn) return;
      this.setButtonAdded(btn, this.blocksByUri.has(uri));
    });
  }

  private setButtonAdded(btn: HTMLElement, added: boolean): void {
    btn.classList.toggle('lb-added', added);
    btn.textContent = added ? '✓' : '+';
    btn.title = added
      ? this.t('lessonBuilder.removeFromLesson')
      : this.t('lessonBuilder.addToLesson');
  }

  private attachAllButtons(root: ParentNode): void {
    const fragments = root.querySelectorAll<HTMLElement>('[data-fragment-id]');
    fragments.forEach((el) => this.attachButtonTo(el));
  }

  private attachButtonTo(el: HTMLElement): void {
    // Verità nel DOM (non flag dataset): se l'elemento persiste ma il suo
    // innerHTML è stato ri-renderizzato (es. radice scheda al cambio
    // Testo⇄Mappa), il bottone va RIATTACCATO — il flag mentiva.
    if (el.querySelector(':scope > .lb-add-btn')) return;
    el.dataset.lbAttached = 'true';

    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'lb-add-btn';
    btn.setAttribute('aria-label', this.t('lessonBuilder.addToLesson'));
    btn.title = this.t('lessonBuilder.addToLesson');
    btn.textContent = '+';

    btn.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      this.captureBlock(el).catch((err) => {
        console.error('[lesson-builder] capture failed:', err);
        this.toast(`Errore: ${err instanceof Error ? err.message : 'aggiunta blocco'}`, 'error');
      });
    });

    el.appendChild(btn);
    // Se il frammento è già nella lezione, nasce marcato come aggiunto (✓).
    const uri = el.dataset.fragmentId;
    if (uri && this.blocksByUri.has(uri)) this.setButtonAdded(btn, true);
  }

  private scanQueued = false;

  private startObserver(): void {
    // Approccio robusto: ad ogni mutazione, schedula uno scan completo del body.
    // Il flag `lbAttached`/`lbMapAttached` impedisce attach doppi.
    this.observer = new MutationObserver(() => {
      if (this.scanQueued) return;
      this.scanQueued = true;
      requestAnimationFrame(() => {
        this.scanQueued = false;
        this.attachAllButtons(document.body);
        this.attachMapCaptureButtons(document.body);
      });
    });
    this.observer.observe(document.body, { childList: true, subtree: true });
  }

  /** Toggle: se il frammento è già nella lezione lo rimuove, altrimenti lo aggiunge. */
  private async captureBlock(el: HTMLElement): Promise<void> {
    if (!this.config?.lessonId) return;
    const fragmentId = el.dataset.fragmentId;
    if (!fragmentId) return;
    const btn = el.querySelector<HTMLElement>(':scope > .lb-add-btn');

    // ── Già presente → RIMUOVI (deseleziona) ──
    const existingBlockId = this.blocksByUri.get(fragmentId);
    if (existingBlockId) {
      const url = `${this.config.apiUrl}/v2/contents/${this.config.lessonId}/blocks/${existingBlockId}`;
      const response = await fetch(url, {
        method: 'DELETE',
        headers: { 'X-User-Id': this.config.userId },
      });
      if (!response.ok) {
        const text = await response.text().catch(() => '');
        throw new Error(`HTTP ${response.status}: ${text}`);
      }
      const data = (await response.json()) as { body_blocks?: { blocks?: unknown[] } };
      this.blocksByUri.delete(fragmentId);
      this.setCount(data.body_blocks?.blocks?.length ?? this.blocksByUri.size);
      if (btn) this.setButtonAdded(btn, false);
      this.pulse();
      this.toast(this.t('lessonBuilder.toast.removed'), 'success');
      return;
    }

    // ── Non presente → AGGIUNGI ──
    const payload = {
      type: this.inferType(el),
      content_html: this.cleanHtml(el),
      source: {
        tool: this.toolId,
        uri: fragmentId,
        captured_at: new Date().toISOString(),
      },
    };
    const url = `${this.config.apiUrl}/v2/contents/${this.config.lessonId}/blocks`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-User-Id': this.config.userId },
      body: JSON.stringify(payload),
    });
    if (!response.ok) {
      const text = await response.text().catch(() => '');
      throw new Error(`HTTP ${response.status}: ${text}`);
    }
    const data = (await response.json()) as {
      body_blocks?: { blocks?: { block_id: string; source?: { uri?: string } }[] };
    };
    const blocks = data.body_blocks?.blocks ?? [];
    // ritrovo il block_id appena creato (ultimo con questo URI)
    const mine = [...blocks].reverse().find((b) => b.source?.uri === fragmentId);
    if (mine) this.blocksByUri.set(fragmentId, mine.block_id);
    this.setCount(blocks.length);
    if (btn) this.setButtonAdded(btn, true);
    this.pulse();
    this.toast('✓ Aggiunto', 'success');
    this.flash(el);
  }

  private pulse(): void {
    this.statusBadge?.classList.add('lb-pulse');
    setTimeout(() => this.statusBadge?.classList.remove('lb-pulse'), 400);
  }

  private inferType(el: HTMLElement): BlockType {
    // Le citazioni di filosofia sono <figure>: vanno mappate a 'quote' PRIMA
    // dei controlli sul tag (altrimenti finirebbero come 'paragraph').
    if (el.matches('.tappa-citazione, .argument-citation')) return 'quote';
    const tag = el.tagName.toLowerCase();
    if (tag === 'h1' || tag === 'h2' || tag === 'h3' || tag === 'h4') return 'heading';
    // I titoli delle macro-sezioni a tendina sono <summary>.
    if (tag === 'summary') return 'heading';
    if (tag === 'blockquote') return 'quote';
    if (tag === 'ul' || tag === 'ol') return 'list';
    if (tag === 'img') return 'image';
    if (tag === 'a') return 'link';
    return 'paragraph';
  }

  private cleanHtml(el: HTMLElement): string {
    let clone = el.cloneNode(true) as HTMLElement;
    // Un <summary> fuori dal suo <details> non ha senso semantico nel hub:
    // il titolo di sezione catturato viaggia come <h2>.
    if (clone.tagName.toLowerCase() === 'summary') {
      const h2 = document.createElement('h2');
      for (const attr of Array.from(clone.attributes)) h2.setAttribute(attr.name, attr.value);
      h2.innerHTML = clone.innerHTML;
      clone = h2;
    }
    clone.querySelectorAll('.lb-add-btn').forEach((n) => n.remove());
    delete (clone.dataset as Record<string, string | undefined>).lbAttached;
    return clone.outerHTML;
  }

  private flash(el: HTMLElement): void {
    el.classList.add('lb-flash');
    setTimeout(() => el.classList.remove('lb-flash'), 600);
  }

  private t(key: string, values?: Record<string, string | number>): string {
    return getI18n().t(key, values);
  }

  private toast(message: string, kind: 'success' | 'error'): void {
    const node = document.createElement('div');
    node.className = `lb-toast lb-toast--${kind}`;
    node.textContent = message;
    document.body.appendChild(node);
    requestAnimationFrame(() => node.classList.add('lb-toast--show'));
    setTimeout(() => {
      node.classList.remove('lb-toast--show');
      setTimeout(() => node.remove(), 300);
    }, 2500);
  }
}

export const lessonBuilderService = new LessonBuilderService();
